import React from "react";

import { cn } from "../../lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export const ABSENT_VALUE = "—";

const PASSIVE_BADGE_TONES = Object.freeze({
  neutral: "border-zinc-300 bg-zinc-100 text-zinc-600 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300",
  info: "border-info/30 bg-semantic-info text-semantic-info",
  success: "border-success/30 bg-semantic-success text-semantic-success",
  warning: "border-warning/30 bg-semantic-warning text-semantic-warning",
  danger: "border-destructive/30 bg-semantic-danger text-semantic-danger",
  category: "border-semantic-category bg-semantic-category text-semantic-category",
  custom: "",
});

export function isAbsentValue(value) {
  return value === null || value === undefined || (typeof value === "string" && value.trim() === "");
}

function isAbsentFullValue(value) {
  return isAbsentValue(value) || (Array.isArray(value) && value.length === 0);
}

export function displayValue(value, fallback = ABSENT_VALUE) {
  return isAbsentValue(value) ? fallback : value;
}

function presentationDate(value) {
  if (isAbsentValue(value)) return null;
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function formatPresentationDate(value, { timeZone } = {}) {
  const dateOnly = typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
  const date = presentationDate(dateOnly ? `${value}T00:00:00Z` : value);
  if (!date) return ABSENT_VALUE;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(timeZone || dateOnly ? { timeZone: timeZone || "UTC" } : {}),
  }).format(date);
}

export function formatPresentationTimestamp(value, { timeZone } = {}) {
  const date = presentationDate(value);
  if (!date) return ABSENT_VALUE;
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    ...(timeZone ? { timeZone } : {}),
  }).format(date);
}

export function PassiveBadge({ children, tone = "neutral", className, ...props }) {
  const knownTone = Object.prototype.hasOwnProperty.call(PASSIVE_BADGE_TONES, tone);
  const resolvedTone = knownTone && (tone !== "custom" || className) ? tone : "neutral";
  return (
    <span
      className={cn(
        "semantic-badge inline-flex max-w-full items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold leading-4",
        PASSIVE_BADGE_TONES[resolvedTone],
        className
      )}
      data-passive-badge="true"
      data-badge-tone={resolvedTone}
      {...props}
    >
      {children}
    </span>
  );
}

export function FullValuePopoverContent({
  value,
  renderListItem = (item) => String(item),
  ariaLabel = "Full value",
  className,
}) {
  if (isAbsentFullValue(value)) return null;

  return (
    <PopoverContent
      align="start"
      sideOffset={6}
      collisionPadding={12}
      aria-label={ariaLabel}
      className={cn(
        "max-h-[min(24rem,var(--radix-popover-content-available-height))] w-fit min-w-[13.75rem] max-w-[min(30rem,calc(100vw-2rem))] overflow-y-auto p-3 text-sm leading-6",
        className
      )}
    >
      {Array.isArray(value) ? (
        <ul className="space-y-1.5" data-full-value-list="true">
          {value.map((item, index) => <li key={index} className="break-words">{renderListItem(item, index)}</li>)}
        </ul>
      ) : <p className="whitespace-pre-wrap break-words" data-full-value-scalar="true">{String(value)}</p>}
    </PopoverContent>
  );
}

export function DataValue({
  value,
  className,
  containment = "wrap",
  title,
  fullValue = false,
  fullValueAriaLabel,
  ...props
}) {
  const rendered = fullValue && Array.isArray(value) && value.length === 0 ? ABSENT_VALUE : displayValue(value);
  const containmentClass = containment === "truncate"
    ? "truncate"
    : containment === "break-all"
      ? "break-all"
      : "break-words";
  const fullValueText = isAbsentValue(value) ? undefined : String(value);
  const enableFullValue = fullValue && containment === "truncate" && !isAbsentFullValue(value);

  if (enableFullValue) {
    return (
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn("block min-w-0 max-w-full border-0 bg-transparent p-0 text-left font-inherit text-inherit", containmentClass, className)}
            aria-label={fullValueAriaLabel || "Show full value"}
            {...props}
          >
            {rendered}
          </button>
        </PopoverTrigger>
        <FullValuePopoverContent value={value} ariaLabel={fullValueAriaLabel || "Full value"} />
      </Popover>
    );
  }

  return (
    <span
      className={cn("min-w-0", containmentClass, className)}
      title={title ?? (containment === "truncate" ? fullValueText : undefined)}
      {...props}
    >
      {rendered}
    </span>
  );
}

export function PrimarySecondaryValue({
  primary,
  secondary,
  compact = false,
  className,
  primaryClassName,
  secondaryClassName,
  primaryContainment = "wrap",
  secondaryContainment = "wrap",
  separator = " - ",
}) {
  const hasSecondary = !isAbsentValue(secondary);
  if (compact) {
    const combined = hasSecondary ? `${displayValue(primary)}${separator}${secondary}` : displayValue(primary);
    return <DataValue value={combined} containment="truncate" className={className} />;
  }
  return (
    <span className={cn("block min-w-0", className)}>
      <DataValue value={primary} containment={primaryContainment} className={cn("block", primaryClassName)} />
      {hasSecondary ? (
        <DataValue value={secondary} containment={secondaryContainment} className={cn("mt-0.5 block text-[10px] text-muted-foreground", secondaryClassName)} />
      ) : null}
    </span>
  );
}

export { PASSIVE_BADGE_TONES };
